import { Link, useLocation } from "@remix-run/react";
import { useEffect, useRef, useState } from "react";

import { useLanguage } from "~/context/LanguageContext";
import { switchLanguagePath } from "~/helpers/navigationHelper";
import common from "~/i18n/common";
import { useT } from "~/i18n/useT";

import { LANGUAGES } from "./languages";

export default function LanguageDropdown() {
  const { lang } = useLanguage();
  const { pathname } = useLocation();
  const t = useT(common);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const active = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="ss-lang" ref={ref}>
      <button
        type="button"
        className="ss-lang__toggle"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t.nav.language}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">{active.flag}</span>
        {active.label}
        <span className="ss-lang__caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open ? (
        <ul className="ss-lang__menu" role="menu">
          {LANGUAGES.map((option) => (
            <li key={option.code} role="none">
              <Link
                role="menuitem"
                // A real link, not a JS state swap: crawlers follow it and it
                // pairs with the hreflang tags in the document head.
                to={switchLanguagePath(pathname, option.code)}
                hrefLang={option.code}
                lang={option.code}
                className={`ss-lang__item${
                  option.code === lang ? " is-active" : ""
                }`}
                onClick={() => setOpen(false)}
              >
                <span className="ss-lang__flag" aria-hidden="true">
                  {option.flag}
                </span>
                {option.name}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
